import type { SchoolData } from '@/context/SchoolContext';
import type {
  TasklistPageAccordionItem,
  TasklistPageBlock,
  TasklistPageJson,
  TasklistPageSection,
  TasklistSliceDisplayConditions,
} from '@/types/tasklistPageJson';

/** CMS section recipe shipped on static `400.json` / WP checklist payload (Replit admin “dev” shape). */
export type WpOverviewField = {
  key?: string;
  kind?: string;
  label?: string;
  displayConditions?: Record<string, unknown>;
  block?: {
    type?: string;
    markdown?: string;
    buttonUrl?: string;
    buttonLabel?: string;
    description?: string;
  };
};

export type WpOverviewSection = {
  id?: string;
  title?: string;
  fields?: WpOverviewField[];
  displayConditions?: Record<string, unknown>;
};

export type SchoolOverviewMergeOptions = {
  isPrivateSchool: boolean;
  hasEnrolledFlex: boolean;
  userRoleVal?: string;
};

function trimHtml(h: unknown): string | undefined {
  return typeof h === 'string' && h.trim() ? h : undefined;
}

function programTypeAllowsSection(
  dc: Record<string, unknown> | undefined,
  hasEnrolledFlex: boolean,
  isPrivateSchool: boolean
): boolean {
  const pt = (dc?.programType as string[] | undefined)?.map((x) => String(x).toLowerCase());
  if (isPrivateSchool && pt?.some((p) => p === 'standard' || p === 'flex')) {
    return false;
  }
  if (!pt?.length) return true;
  if (pt.includes('standard') && pt.includes('flex')) return true;
  if (pt.includes('standard')) return !hasEnrolledFlex;
  if (pt.includes('flex')) return hasEnrolledFlex;
  return true;
}

function fieldProgramHtml(
  field: WpOverviewField,
  hasEnrolledFlex: boolean
): boolean {
  const pt = (field.displayConditions?.programType as string[] | undefined)?.map((x) =>
    String(x).toLowerCase()
  );
  if (!pt?.length) return true;
  if (pt.includes('standard') && pt.includes('flex')) return true;
  if (pt.includes('standard')) return !hasEnrolledFlex;
  if (pt.includes('flex')) return hasEnrolledFlex;
  return true;
}

function wpFieldToSliceConditions(field: WpOverviewField): TasklistSliceDisplayConditions | undefined {
  const dc = field.displayConditions;
  if (!dc) return undefined;
  const userRole = Array.isArray(dc.userRole)
    ? (dc.userRole as unknown[]).map((r) => String(r))
    : undefined;
  const gradeBand = Array.isArray(dc.gradeBand)
    ? (dc.gradeBand as unknown[]).map((r) => String(r))
    : undefined;
  const ols = dc.olsAvailable;
  const lp = dc.launchpadAvailable;
  const out: TasklistSliceDisplayConditions = {};
  if (userRole?.length) out.userRole = userRole;
  if (gradeBand?.length) out.gradeBand = gradeBand;
  if (typeof ols === 'boolean') out.olsAvailable = ols;
  if (typeof lp === 'boolean') out.launchpadAvailable = lp;
  return Object.keys(out).length ? out : undefined;
}

function filterSchoolStartFaqs(
  faqs: NonNullable<SchoolData['onboarding_content']>['school_start_faqs'] | undefined,
  userRoleVal: string | undefined,
  isPrivateSchool: boolean
): TasklistPageAccordionItem[] {
  if (!faqs?.length) return [];
  const userRole = userRoleVal?.toLowerCase() ?? '';
  return faqs
    .filter((faq) => {
      const audience = faq.audience?.toLowerCase?.() ?? 'all';
      if (audience !== 'all' && audience !== userRole) return false;
      const schoolType = faq.school_type?.toLowerCase?.() ?? 'all';
      if (schoolType === 'private' && !isPrivateSchool) return false;
      if (schoolType === 'public' && isPrivateSchool) return false;
      return true;
    })
    .map((faq) => {
      const html = trimHtml(faq.faq_content);
      if (!html || !faq.faq_title?.trim()) return null;
      return { title: faq.faq_title.trim(), htmlParts: [html] } satisfies TasklistPageAccordionItem;
    })
    .filter((x): x is TasklistPageAccordionItem => x != null);
}

function onboardingHtml(oc: Record<string, unknown>, key: string | undefined): string | undefined {
  if (!key || key.startsWith('__')) return undefined;
  return trimHtml(oc[key]);
}

function pushSection(
  sections: TasklistPageSection[],
  title: string,
  blocks: TasklistPageBlock[],
  displayConditions?: TasklistPageSection['displayConditions'],
  id?: string
): void {
  if (!blocks.length) return;
  const anchorId = typeof id === 'string' ? id.trim() : '';
  sections.push({
    title,
    blocks,
    displayConditions,
    ...(anchorId ? { id: anchorId } : {}),
  });
}

function mapQuickLinkBlock(
  block: NonNullable<WpOverviewField['block']>,
  fieldLabel?: string
): TasklistPageBlock[] {
  const out: TasklistPageBlock[] = [];
  if (block.type === 'quickLink') {
    const url = typeof block.buttonUrl === 'string' ? block.buttonUrl.trim() : '';
    if (!url) return out;
    const heading = fieldLabel?.trim() || undefined;
    const desc =
      typeof block.description === 'string' && block.description.trim()
        ? block.description.trim()
        : undefined;
    const label = typeof block.buttonLabel === 'string' ? block.buttonLabel : 'Link';
    out.push({
      type: 'quickLink',
      heading,
      description: desc,
      buttonUrl: url,
      buttonLabel: label,
    });
    return out;
  }
  if (block.type === 'text' && block.markdown?.trim()) {
    out.push({ type: 'text', markdown: block.markdown });
  }
  return out;
}

function buildSectionFromFields(
  section: WpOverviewSection,
  oc: Record<string, unknown>,
  opts: SchoolOverviewMergeOptions
): TasklistPageBlock[] {
  const blocks: TasklistPageBlock[] = [];
  const fields = section.fields ?? [];
  const accordionItems: TasklistPageAccordionItem[] = [];

  let attendanceHtml: string | undefined;
  if (!opts.isPrivateSchool) {
    for (const f of fields) {
      if (f.key !== 'attendance_content_standard' && f.key !== 'attendance_content_flex') continue;
      if (!fieldProgramHtml(f, opts.hasEnrolledFlex)) continue;
      const h = onboardingHtml(oc, f.key);
      if (h) attendanceHtml = h;
    }
  }

  let bodyBuffer: { title: string; parts: string[]; dc?: TasklistSliceDisplayConditions } | null = null;

  const flushBodyBuffer = () => {
    if (!bodyBuffer?.parts.length) {
      bodyBuffer = null;
      return;
    }
    accordionItems.push({
      title: bodyBuffer.title,
      htmlParts: [...bodyBuffer.parts],
      ...(bodyBuffer.dc ? { displayConditions: bodyBuffer.dc } : {}),
    });
    bodyBuffer = null;
  };

  for (const field of fields) {
    if (field.block && field.key?.startsWith('__')) {
      flushBodyBuffer();
      blocks.push(...mapQuickLinkBlock(field.block, field.label));
      continue;
    }

    if (field.key === 'attendance_content_standard' || field.key === 'attendance_content_flex') {
      continue;
    }

    const html = onboardingHtml(oc, field.key);
    const label = typeof field.label === 'string' ? field.label.trim() : '';
    const isBody = field.kind === 'body';

    if (isBody) {
      if (!fieldProgramHtml(field, opts.hasEnrolledFlex) || !html) continue;
      if (label) {
        flushBodyBuffer();
        bodyBuffer = { title: label, parts: [html], dc: wpFieldToSliceConditions(field) };
      } else if (bodyBuffer) {
        bodyBuffer.parts.push(html);
      } else {
        bodyBuffer = { title: 'Details', parts: [html] };
      }
      continue;
    }

    if (field.key === 'quizzes_and_tests' && html) {
      flushBodyBuffer();
      accordionItems.push({
        title: label || 'Quizzes & Tests',
        htmlParts: [html],
        displayConditions: { userRole: ['student'] },
      });
      continue;
    }

    if (field.key === 'quizzes_and_tests_learning_coach' && html) {
      flushBodyBuffer();
      accordionItems.push({
        title: 'Learning Coach Quizzes & Tests',
        htmlParts: [html],
        displayConditions: wpFieldToSliceConditions(field) ?? { userRole: ['learningCoach'] },
      });
      continue;
    }

    if (label && html) {
      flushBodyBuffer();
      const dc = wpFieldToSliceConditions(field);
      accordionItems.push({
        title: label,
        htmlParts: [html],
        ...(dc ? { displayConditions: dc } : {}),
      });
      continue;
    }

    if (html) {
      flushBodyBuffer();
      blocks.push({ type: 'html', html });
    }
  }

  flushBodyBuffer();

  const mergedAccordion: TasklistPageAccordionItem[] = [];
  if (attendanceHtml) {
    mergedAccordion.push({ title: 'Attendance', htmlParts: [attendanceHtml] });
  }
  mergedAccordion.push(...accordionItems);

  for (const item of mergedAccordion) {
    blocks.push({
      type: 'htmlSubsection',
      title: item.title,
      htmlParts: [...item.htmlParts],
      ...(item.displayConditions ? { displayConditions: item.displayConditions } : {}),
    });
  }

  return blocks;
}

/** Clubs & Workshops CTA panel — aligned with `400.json` / Replit school overview. */
export const CLUBS_WORKSHOPS_CTA_PANEL_TITLE = 'Explore Clubs and Activities';
export const CLUBS_WORKSHOPS_CTA_PANEL_LEAD = 'Keep checking back for updates!';
export const CLUBS_WORKSHOPS_CTA_BUTTON_LABEL = 'Explore Clubs & Activities';

function clubsBlocksFromSchool(school: SchoolData | null | undefined): TasklistPageBlock[] {
  const cw = school?.clubs_workshops;
  if (!cw || typeof cw !== 'object') return [];
  const rec = cw as Record<string, unknown>;
  const desc = trimHtml(rec.clubs_workshops_description);
  const link = typeof rec.clubs_workshops_link === 'string' ? rec.clubs_workshops_link.trim() : '';
  const blocks: TasklistPageBlock[] = [];
  if (desc) blocks.push({ type: 'html', html: desc });
  if (link) {
    blocks.push({
      type: 'ctaPanel',
      title: CLUBS_WORKSHOPS_CTA_PANEL_TITLE,
      description: CLUBS_WORKSHOPS_CTA_PANEL_LEAD,
      buttonUrl: link,
      buttonLabel: CLUBS_WORKSHOPS_CTA_BUTTON_LABEL,
    });
  }
  return blocks;
}

/**
 * Puts `clubs-workshops` then `faqs` at the end of the section list (in that order), regardless of
 * how those two entries appear in CMS `wpSections`. Matches Replit school-overview layout without
 * relying on JSON ordering for those blocks.
 */
export function reorderSchoolOverviewWpSections(sections: WpOverviewSection[]): WpOverviewSection[] {
  let clubs: WpOverviewSection | undefined;
  let faqs: WpOverviewSection | undefined;
  const rest: WpOverviewSection[] = [];
  for (const raw of sections) {
    if (!raw || typeof raw !== 'object') continue;
    const sid = typeof raw.id === 'string' ? raw.id : '';
    if (sid === 'clubs-workshops') {
      clubs = raw;
      continue;
    }
    if (sid === 'faqs') {
      faqs = raw;
      continue;
    }
    rest.push(raw);
  }
  const tail: WpOverviewSection[] = [];
  if (clubs) tail.push(clubs);
  if (faqs) tail.push(faqs);
  return [...rest, ...tail];
}

/** Section `title` from CMS `400.json` / WP school overview (used for stable ordering only). */
export const SCHOOL_OVERVIEW_CLUBS_SECTION_TITLE = 'Clubs & Workshops';

function isSchoolOverviewFaqSectionTitle(title: string): boolean {
  const t = title.trim();
  return /^frequently asked questions$/i.test(t) || /^faqs$/i.test(t);
}

/**
 * When CMS ships pre-built `content`, FAQ can appear before Clubs; normalize so Clubs & Workshops
 * always precedes the FAQ section (matches Replit / merged `wpSections` tail order).
 */
export function reorderClubsWorkshopsSectionBeforeFaqs(sections: TasklistPageSection[]): TasklistPageSection[] {
  const clubsI = sections.findIndex((s) => s.title.trim() === SCHOOL_OVERVIEW_CLUBS_SECTION_TITLE);
  const faqI = sections.findIndex((s) => isSchoolOverviewFaqSectionTitle(s.title));
  if (clubsI === -1 || faqI === -1 || clubsI < faqI) {
    return sections;
  }
  const out = sections.slice();
  const [clubsSec] = out.splice(clubsI, 1);
  const insertAt = out.findIndex((s) => isSchoolOverviewFaqSectionTitle(s.title));
  if (insertAt === -1) return sections;
  out.splice(insertAt, 0, clubsSec);
  return out;
}

/**
 * Builds `content` for page `400` from `wpSections` + live school API fields (same grouping as
 * {@link AboutAttendingSchool} / Replit document school-overview).
 */
export function mergeSchoolOverviewPageFromWp(
  page: TasklistPageJson,
  school: SchoolData | null | undefined,
  opts: SchoolOverviewMergeOptions
): TasklistPageJson {
  if (page.content && page.content.length > 0) {
    const content = reorderClubsWorkshopsSectionBeforeFaqs(page.content);
    return content === page.content ? page : { ...page, content };
  }

  const oc = (school?.onboarding_content ?? {}) as Record<string, unknown>;
  const sections: TasklistPageSection[] = [];
  const wpSections = reorderSchoolOverviewWpSections((page.wpSections ?? []) as WpOverviewSection[]);

  for (const raw of wpSections) {
    if (!raw || typeof raw !== 'object') continue;
    const sec = raw as WpOverviewSection;
    const sid = typeof sec.id === 'string' ? sec.id : '';
    if (sid === 'quick-links') continue;

    if (!programTypeAllowsSection(sec.displayConditions as Record<string, unknown> | undefined, opts.hasEnrolledFlex, opts.isPrivateSchool)) {
      continue;
    }

    const title = typeof sec.title === 'string' ? sec.title.trim() : '';
    if (!title) continue;

    if (sid === 'faqs') {
      const blocks: TasklistPageBlock[] = [];
      const lead = trimHtml(page.wpFaq?.calloutMarkdown);
      /** "Come back soon" teaser — hide once `onboarding_content.school_info_ready` is true from the school API. */
      const schoolInfoReady = oc.school_info_ready === true;
      if (lead && !schoolInfoReady) blocks.push({ type: 'callout', text: lead });
      const faqRows = oc.school_start_faqs as NonNullable<SchoolData['onboarding_content']>['school_start_faqs'] | undefined;
      const faqItems = filterSchoolStartFaqs(faqRows, opts.userRoleVal, opts.isPrivateSchool);
      if (faqItems.length) blocks.push({ type: 'accordion', items: faqItems });
      pushSection(sections, title, blocks, undefined, sid);
      continue;
    }

    if (sid === 'clubs-workshops') {
      const apiBlocks = clubsBlocksFromSchool(school);
      if (!apiBlocks.length) continue;
      pushSection(sections, title, apiBlocks, undefined, sid);
      continue;
    }

    if (sid === 'testing') {
      const html = onboardingHtml(oc, 'standardized_testing');
      if (!html || opts.userRoleVal !== 'LEARNING_COACH') continue;
      pushSection(sections, title, [{ type: 'html', html }], { userRole: ['learningCoach'] }, sid);
      continue;
    }

    const blocks = buildSectionFromFields(sec, oc, opts);
    pushSection(
      sections,
      title,
      blocks,
      wpFieldToSliceConditions({ displayConditions: sec.displayConditions }),
      sid
    );
  }

  return {
    ...page,
    content: reorderClubsWorkshopsSectionBeforeFaqs(sections),
  };
}

